import { useState } from "react";
import { useEstadoNaUrl } from "@/lib/estado-na-url";
import { useNavegacao } from "@/lib/navegacao";
import { Button } from "@/components/ui/button";
import { Plus, MessageCircle, Phone, Users2, Send } from "lucide-react";
import { PageHeader } from "@/components/layout/page-header";
import { PageSearch } from "@/components/layout/page-toolbar";
import { Segmented } from "@/components/layout/segmented";
import { PageSkeleton } from "@/components/data/page-skeleton";
import { EmptyState, NadaEncontrado } from "@/components/data/empty-state";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/auth-context";
import { useCompradoresProdutor } from "@/hooks/use-mercado";
import { ProdutorShell } from "@/components/mercado/produtor-shell";
import { StatusChip } from "@/components/mercado/status-chip";
import { labelPerfilComprador } from "@shared/schema";
import { DialogComprador } from "@/components/mercado/dialog-comprador";
import { DialogReenviar } from "@/components/mercado/dialog-reenviar";
import { ModalFormalizar } from "@/components/mercado/formalizar-comprador";
import { AvisoNovoComprador } from "@/components/mercado/aviso-novo-comprador";
import { fmtMoedaBR, fmtToneladas, fmtMesAno, iniciais } from "@/lib/formato-mercado";
import { combina } from "@/lib/busca";
import type { CompradorProdutorDTO, EstadoComprador } from "@shared/mercado";

type FiltroEstado = "todos" | "ativos" | "sem_acesso";

/**
 * Corretores: quem compra as suas cargas — cadastro, vínculo e convite. As
 * vendas ficam em A receber. Separar "quem é" de "quanto me deve" é o motivo
 * desta tela existir no lugar de "Meus corretores" — que, além de tudo, chamava de corretor quem é distribuidor.
 */
// ⚠️ Constantes de MÓDULO (deps de useMemo dentro do hook).
const PADRAO_COMPRADORES = { estado: "todos", q: "" };
const PERMITIDOS_COMPRADORES = { estado: ["todos", "ativos", "sem_acesso"] } as const;

export default function MercadoCompradores() {
  const { user } = useAuth();
  // Recorte na URL: abrir um comprador e voltar devolve a mesma lista filtrada.
  const { estado: filtros, definir } = useEstadoNaUrl(PADRAO_COMPRADORES, PERMITIDOS_COMPRADORES);
  const estado = filtros.estado as FiltroEstado;
  const busca = filtros.q;
  // Dialog aberto é estado de UI do momento, não recorte: fica no useState.
  const [novo, setNovo] = useState(false);
  // A lista inteira vem uma vez; estado e busca filtram NO CLIENTE (ver lib/busca).
  const { data, isLoading } = useCompradoresProdutor({}, user?.orgTipo !== "COMPRADOR");
  const todos = data ?? [];

  const contagem = {
    todos: todos.length,
    ativos: todos.filter(c => c.estado === "ATIVO").length,
    sem_acesso: todos.filter(c => c.estado !== "ATIVO").length,
  };
  const porEstado = estado === "ativos"
    ? todos.filter(c => c.estado === "ATIVO")
    : estado === "sem_acesso" ? todos.filter(c => c.estado !== "ATIVO") : todos;
  // Sem filtro de perfil: a lista da fazenda tem uma dúzia de nomes e quase
  // todos são corretor — um segundo segmentado aí era um eixo a mais para
  // ocupar a tela sem separar nada. Quem procura um nome usa a busca.
  const visiveis = porEstado.filter(c => combina(busca, c.nome, c.telefoneWhatsapp, c.cidade));

  return (
    <ProdutorShell>
      <PageHeader
        titulo="Compradores"
        descricao="Quem compra as suas cargas — corretores e distribuidores. Cadastro, vínculo e convite; as vendas ficam em A receber."
        empilharQuandoEstreito
        acao={
          <Button onClick={() => setNovo(true)} data-testid="button-novo-comprador">
            <Plus className="h-4 w-4 mr-1" />Novo comprador
          </Button>
        }
      />

      <AvisoNovoComprador />

      {/* Não é a PageToolbar: ela rola de lado no celular, e aqui nada rola de
          lado. O segmentado ocupa a linha inteira com três pastilhas iguais e a
          busca fica embaixo; no desktop os dois voltam para a mesma linha. */}
      <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <Segmented<FiltroEstado>
          valor={estado}
          onChange={v => definir({ estado: v })}
          testId="seg-estado-comprador"
          className="w-full md:w-auto [&>button]:flex-1 md:[&>button]:flex-none"
          opcoes={[
            { id: "todos", label: `Todos · ${contagem.todos}` },
            { id: "ativos", label: `Ativos · ${contagem.ativos}` },
            { id: "sem_acesso", label: `Sem acesso · ${contagem.sem_acesso}` },
          ]}
        />
        <PageSearch valor={busca} onChange={v => definir({ q: v })} placeholder="Nome ou telefone" testId="input-busca-compradores" className="w-full sm:w-[200px]" />
      </div>

      {isLoading ? (
        <PageSkeleton variante="cards" linhas={3} />
      ) : todos.length === 0 ? (
        <EmptyState
          icone={Users2}
          titulo="Nenhum comprador cadastrado"
          descricao="Cadastre quem compra de você. O mesmo cadastro aparece nas vendas do Cacho."
          acao={<Button onClick={() => setNovo(true)}><Plus className="h-4 w-4 mr-1" />Novo comprador</Button>}
        />
      ) : visiveis.length === 0 && busca ? (
        <NadaEncontrado termo={busca} />
      ) : (
        // Grade por PREENCHIMENTO, não por contagem: com três colunas fixas, uma
        // janela de 1142px dava ~250px por card, e aí o nome do corretor truncava e
        // os botões quebravam em três linhas. Com `auto-fill` de mínimo 300px dá 1
        // coluna no celular, 2 em ~890px de conteúdo e 3 a partir de ~1180px.
        <div className="grid grid-cols-[repeat(auto-fill,minmax(300px,1fr))] gap-3">
          {visiveis.map(c => <CardComprador key={c.compradorId} comprador={c} />)}
          <button
            type="button"
            onClick={() => setNovo(true)}
            className="rounded-lg border border-dashed min-h-[120px] flex flex-col items-center justify-center gap-1 text-sm text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-colors"
            data-testid="card-novo-comprador"
          >
            <Plus className="h-5 w-5" />
            novo comprador
          </button>
        </div>
      )}

      {novo && <DialogComprador onFechar={() => setNovo(false)} />}
    </ProdutorShell>
  );
}

const CHIP_ESTADO: Record<EstadoComprador, { kind: "pago" | "warn" | "aberta"; label: string }> = {
  ATIVO: { kind: "pago", label: "Ativo" },
  CONVIDADO: { kind: "warn", label: "Convidado" },
  SEM_ACESSO: { kind: "aberta", label: "Sem acesso" },
};

function CardComprador({ comprador: c }: { comprador: CompradorProdutorDTO }) {
  const { abrir } = useNavegacao();
  const { toast } = useToast();
  const [reenviar, setReenviar] = useState(false);
  const [formalizar, setFormalizar] = useState(false);
  const [editando, setEditando] = useState(false);
  const chip = CHIP_ESTADO[c.estado];
  const waLink = c.telefoneWhatsapp ? `https://wa.me/55${c.telefoneWhatsapp}` : null;
  const temRomaneios = c.romaneios > 0;
  /**
   * Sem telefone, NADA no card funciona: não há WhatsApp, não há convite, não
   * há link de romaneio para mandar. A ação primária desse cadastro é
   * completá-lo — oferecer "Convidar pelo WhatsApp" a quem não tem número é um
   * botão que só sabe dizer que não dá.
   */
  const semTelefone = !c.telefoneWhatsapp;
  const abrirDetalhe = () => abrir(`/mercado/compradores/${c.compradorId}`);

  /**
   * Convidar = o fluxo de formalizar do C1, nunca um segundo caminho:
   * - já existe romaneio formalizado e nunca enviado (o backfill do C2) → manda
   *   o link daquele romaneio;
   * - não existe, mas há venda EMITIDA → formaliza essa venda e, formalizada,
   *   o card passa a oferecer o envio;
   * - não há venda emitida → o convite precisa de uma venda: leva ao Cacho.
   */
  function handleConvidar() {
    if (c.ultimoCompartilhamentoId) { setReenviar(true); return; }
    if (c.vendaParaConvidarId) { setFormalizar(true); return; }
    toast({
      title: "Convide a partir de uma venda",
      description: `O convite vai junto de um romaneio. Emita uma venda para ${c.nome} no Cacho e formalize — o link chega com ela.`,
    });
    abrir("/cacho/vendas/novo");
  }

  return (
    <article className="rounded-lg border bg-card shadow-xs p-3 flex flex-col gap-3" data-testid={`comprador-${c.compradorId}`}>
      <div className="flex items-start gap-3">
        {/* Avatar e NOME são a porta do detalhe, em qualquer estado. Com "uma ação
            por estado", o card de quem está Sem acesso ficou sem botão de detalhe
            — e aí não havia caminho nenhum até `/mercado/corretores/:id`. */}
        <button
          type="button"
          onClick={abrirDetalhe}
          className="flex items-start gap-3 min-w-0 flex-1 text-left rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label={`Abrir ${c.nome}`}
          data-testid={`abrir-corretor-${c.compradorId}`}
        >
          <span className="h-10 w-10 rounded-full bg-primary/10 text-primary font-bold text-sm flex items-center justify-center shrink-0">
            {iniciais(c.nome)}
          </span>
          {/* O NOME NUNCA TRUNCA. Quando ele e o chip não cabem na mesma linha, é o
              CHIP que desce (`flex-wrap`) — "Humb…" não identifica ninguém, e o
              estado do vínculo se lê igual uma linha abaixo. */}
          <div className="min-w-0 flex-1 flex flex-wrap items-start gap-x-2 gap-y-1">
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-sm break-words hover:text-primary">{c.nome}</p>
              <p className="text-xs text-muted-foreground">
                {c.telefoneMascarado ?? "sem telefone"}
                {c.desde && <> · desde {fmtMesAno(c.desde)}</>}
              </p>
            </div>
            <StatusChip kind={chip.kind} testId={`chip-estado-${c.compradorId}`}>{chip.label}</StatusChip>
            {/* Só DISTRIBUIDOR ganha chip. Corretor é o padrão do cadastro e a
                esmagadora maioria da lista: um chip em todo mundo é ruído que
                esconde justamente o que distingue um do outro. */}
            {c.perfil === "DISTRIBUIDOR" && (
              <StatusChip kind="aberta" testId={`chip-perfil-${c.compradorId}`}>
                {labelPerfilComprador(c.perfil)}
              </StatusChip>
            )}
          </div>
        </button>
      </div>

      {/* 2×2 e não 4 em linha: com três cards por linha no desktop cada card tem
          ~280px, e "R$ 268.400,50" não cabe num quarto disso sem cortar dígito. */}
      <dl className="grid grid-cols-2 gap-x-3 gap-y-2">
        <Numero rotulo="Romaneios" valor={String(c.romaneios)} />
        <Numero rotulo="Volume" valor={fmtToneladas(c.volumeKg)} />
        <Numero rotulo="Comprado" valor={fmtMoedaBR(c.comprado)} />
        <Numero rotulo="Em aberto" valor={fmtMoedaBR(c.emAberto)} tom={c.emAberto > 0 ? "atencao" : undefined} />
      </dl>

      {/* A PRIMÁRIA é a do estado; "Ver romaneios" é secundária e existe sempre
          que houver romaneio (com zero, levaria a uma tela vazia). Sem acesso COM
          romaneio pede "Enviar romaneio pelo WhatsApp", não "Convidar": o convite
          É o link da nota — foi assim que o C2 definiu a criação de acesso. */}
      <div className="grid grid-cols-2 gap-2 [&>*]:min-h-10 [&>*]:h-auto [&>*]:whitespace-normal [&>*]:py-1.5 [&>*]:text-xs">
        {semTelefone ? (
          <>
            <Button
              size="sm"
              className={temRomaneios ? undefined : "col-span-2"}
              onClick={() => setEditando(true)}
              data-testid={`button-adicionar-telefone-${c.compradorId}`}
            >
              <Phone className="h-4 w-4 mr-1.5" />Adicionar telefone
            </Button>
            {temRomaneios && (
              <Button variant="outline" size="sm" onClick={abrirDetalhe} data-testid={`button-ver-romaneios-${c.compradorId}`}>
                Ver romaneios
              </Button>
            )}
          </>
        ) : c.estado === "ATIVO" ? (
          <>
            <Button size="sm" onClick={abrirDetalhe} data-testid={`button-ver-romaneios-${c.compradorId}`}>
              Ver romaneios
            </Button>
            {waLink ? (
              <Button variant="outline" size="sm" asChild>
                <a href={waLink} target="_blank" rel="noopener noreferrer"><MessageCircle className="h-4 w-4 mr-1.5" />WhatsApp</a>
              </Button>
            ) : (
              <Button variant="outline" size="sm" disabled title="Sem telefone cadastrado"><MessageCircle className="h-4 w-4 mr-1.5" />WhatsApp</Button>
            )}
          </>
        ) : (
          <>
            <Button
              size="sm"
              className={temRomaneios ? undefined : "col-span-2"}
              onClick={c.estado === "CONVIDADO" ? () => setReenviar(true) : handleConvidar}
              disabled={c.estado === "CONVIDADO" && !c.ultimoCompartilhamentoId}
              data-testid={c.estado === "CONVIDADO" ? `button-reenviar-convite-${c.compradorId}` : `button-convidar-${c.compradorId}`}
            >
              {c.estado === "CONVIDADO" ? (
                <><Send className="h-4 w-4 mr-1.5" />Reenviar convite</>
              ) : temRomaneios ? (
                <><Send className="h-4 w-4 mr-1.5" />Enviar romaneio pelo WhatsApp</>
              ) : (
                <><MessageCircle className="h-4 w-4 mr-1.5" />Convidar pelo WhatsApp</>
              )}
            </Button>
            {temRomaneios && (
              <Button variant="outline" size="sm" onClick={abrirDetalhe} data-testid={`button-ver-romaneios-${c.compradorId}`}>
                Ver romaneios
              </Button>
            )}
          </>
        )}
      </div>

      {editando && (
        <DialogComprador
          comprador={{ id: c.compradorId, nome: c.nome, telefone: c.telefoneWhatsapp, cidade: c.cidade, perfil: c.perfil }}
          onFechar={() => setEditando(false)}
        />
      )}
      {reenviar && c.ultimoCompartilhamentoId && (
        <DialogReenviar
          compartilhamentoId={c.ultimoCompartilhamentoId}
          titulo={c.estado === "CONVIDADO" ? `Reenviar convite para ${c.nome}` : `Enviar romaneio para ${c.nome}`}
          descricao="O link do romaneio vai junto — é por ele que o comprador cria o acesso."
          primeiroEnvio={c.estado === "SEM_ACESSO"}
          onFechar={() => setReenviar(false)}
        />
      )}
      {formalizar && c.vendaParaConvidarId && (
        <ModalFormalizar
          vendaId={c.vendaParaConvidarId}
          compradorNome={c.nome}
          telefoneCadastro={c.telefoneWhatsapp}
          onFechar={() => setFormalizar(false)}
          onPronto={() => {
            setFormalizar(false);
            toast({ title: "Romaneio formalizado!", description: "Agora toque em Convidar de novo para mandar o link pelo WhatsApp." });
          }}
        />
      )}
    </article>
  );
}

function Numero({ rotulo, valor, tom }: { rotulo: string; valor: string; tom?: "atencao" }) {
  return (
    <div className="min-w-0 rounded-md bg-muted/40 px-2 py-1.5">
      <dt className="text-[10px] uppercase tracking-wider text-muted-foreground truncate">{rotulo}</dt>
      <dd className={`font-mono text-sm font-semibold tabular-nums whitespace-nowrap truncate ${tom === "atencao" ? "text-atencao" : ""}`} title={valor}>{valor}</dd>
    </div>
  );
}
